import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectShowsTakeoverAlert } from '../../redux/features/ai/ai.selector';
import { AgentMode } from '../../redux/features/agentMode/agentMode.constants';
import { selectAgentMode } from '../../redux/features/agentMode/agentMode.selector';
import { setAgentMode } from '../../redux/features/agentMode/agentMode.slice';
import { selectIsShadowModeEnabled } from '../../redux/features/config/config.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import logger from '../../utils/logger';

/**
 * HITL-1650 — when the AI asks for a human, put the agent where they can be one.
 *
 * The Pulse band blinks red and says "take over now". Until now that was all it
 * did: the agent still had to find the mode switch and choose `Intercept` before
 * the console would let them touch anything — in the seconds where the customer
 * is waiting and the AI has already given up. This closes that gap.
 *
 * ## What it does NOT do
 *
 * It arms `Intercept`; it does not take over. `setAgentMode` sets the mode and
 * nothing else: no `agent_interception` goes out, no TTS, no `sendOrder`, and
 * `isAIAutoMode` is untouched — that flag is the AI's own control state, and
 * mutating it from the console to chase a display state is what caused the
 * dropped-order incident behind MR !1534. HITL-1604's distinction is the whole
 * point here: `Intercept` selected with nothing sent is permission without
 * consequence. The agent arrives with the gates open and still chooses whether
 * to act.
 *
 * It also emits no `agent_mode_change` metric. That metric is written at the
 * switch (`AgentModeSwitch`) precisely because it means "the agent chose", and
 * this transition is not the agent choosing. `usePromoteDraftCart` sets the mode
 * the same way for the same reason.
 *
 * ## Why the blink keeps blinking afterwards
 *
 * `selectShowsTakeoverAlert` is gated on `isAIAutoMode`, which only clears when
 * the agent actually intercepts. So the band goes on asking after this fires,
 * and that is correct: the request is answered by the agent ACTING, not by a
 * mode flipping underneath them. Silencing it here would mean writing
 * `isAIAutoMode`, which is the one thing this must not do.
 *
 * ## One shot per session
 *
 * Latched on the session id, not on a boolean. While `Intercept` is merely armed
 * the switch still lets the agent go back to `Shadow` (`isLocked` is
 * `engaged && intercept`), and an effect that re-fired on the still-true alert
 * would drag them straight back — the console fighting the agent over their own
 * console. One attempt per session; after that the decision is theirs.
 */
const useAutoInterceptOnTakeover = () => {
  const dispatch = useAppDispatch();
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);
  const showsTakeoverAlert = useAppSelector(selectShowsTakeoverAlert);
  const mode = useAppSelector(selectAgentMode);
  const sessionId = useAppSelector(selectSessionId);

  const handledSessionRef = useRef<string | null>(null);

  useEffect(() => {
    // Flag off is production today: the blink asks, and the agent answers it by
    // hand exactly as they do now.
    if (!isShadowModeEnabled) return;
    // `sessionId` is not re-checked here: `selectShowsTakeoverAlert` already
    // requires one, and a second copy of that term would be dead code pretending
    // to be a guard. It is still read below, as the latch's key.
    if (!showsTakeoverAlert) return;
    if (handledSessionRef.current === sessionId) return;

    // Latched before the mode check, so a session where the agent was ALREADY in
    // `Intercept` when the AI escalated is spent too — otherwise stepping back to
    // `Shadow` later in that same session would let this fire on the still-true
    // alert and overrule them.
    handledSessionRef.current = sessionId;
    if (mode === AgentMode.intercept) return;

    dispatch(setAgentMode(AgentMode.intercept));
    logger.info({
      message: 'AI asked for a takeover — armed Intercept for the agent',
      moreInfo: `session ${sessionId}`,
    });
    // `mode` is deliberately absent: this must run when the ALERT arrives, not
    // every time the agent moves the switch afterwards. It is read as the latest
    // render's value at that moment, which is what the guard above needs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isShadowModeEnabled, showsTakeoverAlert, sessionId]);
};

export default useAutoInterceptOnTakeover;
